import { Injectable } from '@angular/core';
import { Invoice } from '../models/invoice';
import { EntityCollectionServiceBase, EntityCollectionServiceElementsFactory } from '@ngrx/data';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { InvoiceQuery } from '../models/invoice-query';
import { environment } from '../../environments/environment';
import { InvoiceStatusEnum } from '../enum/invoice-status.enum';
import { InvoiceTypeEnum } from '../enum/invoice-type.enum';
import { PageResponse } from '../interfaces/page-response';

@Injectable({ providedIn: 'root' })
export class InvoiceService extends EntityCollectionServiceBase<Invoice> {

    private loaded = false;

    constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory, private http: HttpClient) {
        super('Invoice', serviceElementsFactory);
    }

    get(id: string): Observable<Invoice> {
        return this.entityMap$.pipe(
            map((entities) => entities[id]),
            switchMap((value) => value ? of(value) : this.getByKey(id))
        );
    }

    query(query: InvoiceQuery): Observable<PageResponse<Invoice>> {
        let params = new HttpParams()
            .set('page', query.pageNumber?.toString() || '1')
            .set('itemsPerPage', query.itemsPerPage.toString());
        if (query.sortColumn) {
            params = params.set('order[' + query.sortColumn + ']', query.sortDescending ? 'DESC' : 'ASC');
        }
        if (query.reference) {
            params = params.set('reference', query.reference);
        }
        if (query.paid === false) {
            params = params.set('outstanding[neq]', 0);
        }
        if (query.paid === true) {
            params = params.set('outstanding[eq]', 0);
        }
        if (query.type) {
            params = params.set('type', query.type);
        }
        const headers = new HttpHeaders({ Accept: 'application/ld+json' });
        return this.http.get<any>(`${environment.apiUrl}/invoices`, { headers, params }).pipe(
            map(r => ({
                totalItems: r['hydra:totalItems'],
                page: r['hydra:member']
            }))
        );
    }

    all(): Observable<Invoice[]> {
        if (!this.loaded) {
            this.load();
            this.loaded = true;
        }
        return this.entities$;
    }

    payable(): Observable<Invoice[]> {
        return this.query({ paid: false, itemsPerPage: 100 }).pipe(
            map(response => response.page),
            map(invoices => invoices.filter(
                (invoice) => (invoice.status !== InvoiceStatusEnum.CANCELLED) && (invoice.status !== InvoiceStatusEnum.REFERENCE)
            )),
            map(invoices => invoices.filter(
                (invoice) => (invoice.type === InvoiceTypeEnum.INVOICE || invoice.type === InvoiceTypeEnum.CREDIT_NOTE)
            ))
        );
    }
}
